// Interfaces
import {
  ApexGetDiscussionEpicRes200,
  ApexGetDiscussionEpicRes444,
} from "../core/types/ApexApi";
import { DiscussionJsonPage } from "../core/interfaces/JsonPage";
import User from "../core/interfaces/User";
import Discussion from "../core/interfaces/Discussion";

// Components
import "../components/jira-dialog/jiraDialog";
import CCCJiraDialog from "../components/jira-dialog/jiraDialog";
import "../components/bug-db-dialog/bugDbDialog";
import CCCBugDbDialog from "../components/bug-db-dialog/bugDbDialog";

// Models
import UserModel from "../models/UserModel";
import DiscussionModel from "../models/DiscussionModel";
import ApexModel from "../models/ApexModel";
import JiraIntegrationModel from "../models/JiraIntegrationModel";
import BugDbIntegrationModel from "../models/BugDbIntegrationModel";

export default class ERIntegrationController {
  self: User;
  pageMetadata: DiscussionJsonPage;
  discussion: Discussion;
  jiraEnableForStatuses = [
    "Under Oracle Review",
    "Planned",
    "Option for Future Release",
  ];
  bugDbEnableForStatuses = [
    "Under Oracle Review",
    "Planned",
    "Option for Future Release",
  ];
  $jiraDialog: CCCJiraDialog;
  $bugDbDialog: CCCBugDbDialog;

  public init(): void {
    console.log("ERIntegration controller.");
    // Validate if user is Moderator or Admin.
    UserModel.getCurrentUserCached()
      .then((user: User) => {
        console.log("  ERIntegration - Current user", user);
        this.self = user;
        if (
          user.roles.some(
            (role: string) => role === "ADMINISTRATOR" || role === "MODERATOR"
          )
        ) {
          return ApexModel.hasAccess();
        } else {
          throw "  ERIntegration - Is not a moderator or admin.";
        }
      })
      // Validate if user is in Oracle Network.
      .then((hasAccess: boolean) => {
        if (hasAccess) {
          return DiscussionModel.getDiscussionFromJsonPageCached();
        } else {
          throw "  ERIntegration - Admin/Moderator is not in Oracle network.";
        }
      })
      // Get page metadata and validate if it's an Idea.
      .then((pageMetadata: DiscussionJsonPage) => {
        console.log("  ERIntegration - Page metadata", pageMetadata);
        this.pageMetadata = pageMetadata;
        if (
          !pageMetadata.Discussion ||
          !pageMetadata.Discussion.Type ||
          pageMetadata.Discussion.Type.toUpperCase() !== "IDEA"
        ) {
          throw "  ERIntegration - It's not an Idea.";
        }
        if (
          pageMetadata.Discussion.XTags &&
          pageMetadata.Discussion.XTags.Status
        ) {
          let enable = false;
          pageMetadata.Discussion.XTags.Status.forEach((status) => {
            const enableForStatuses = this.jiraEnableForStatuses.concat(
              this.bugDbEnableForStatuses
            );
            if (enableForStatuses.some((enable) => enable === status.Name)) {
              enable = true;
            }
          });
          if (!enable) {
            throw "  ERIntegration - Not allowed for this idea status.";
          }
        } else {
          throw "  ERIntegration - The idea doesn't have status.";
        }
        return DiscussionModel.getDiscussionCached(
          pageMetadata.Discussion.DiscussionID
        );
      })
      .then((discussion) => {
        this.discussion = discussion;
        this.addModeratorButtons();
      })
      .catch((error) => {
        console.log(error);
      });
  }

  private async addModeratorButtons(): Promise<void> {
    const $menuOptions = document.querySelector(".Flyout.MenuItems.list-reset");
    if ($menuOptions) {
      let displayJira = false;
      const displayBugDB = false;
      if (
        this.pageMetadata.Discussion &&
        this.pageMetadata.Discussion.XTags &&
        this.pageMetadata.Discussion.XTags.Status
      ) {
        this.pageMetadata.Discussion.XTags.Status.forEach((status) => {
          if (
            this.jiraEnableForStatuses.some((enable) => enable === status.Name)
          ) {
            displayJira = true;
          }
          // if (
          //   this.bugDbEnableForStatuses.some((enable) => enable === status.Name)
          // ) {
          //   displayBugDB = true;
          // }
        });
        // Validate if category has JIRA projects configured.
        if (displayJira) {
          const categoryJira = await JiraIntegrationModel.getCategoryJira(
            this.discussion.categoryId
          );
          if (categoryJira.status !== 200) {
            displayJira = false;
            console.log(
              "  ERIntegration.JIRA - No JIRA projects mapped to this category."
            );
          }
        }
      }
      if (displayJira || displayBugDB) {
        // Create divider element.
        const $dividerLi = document.createElement("li");
        $dividerLi.setAttribute("role", "presentation");
        $dividerLi.classList.add("divider-item1");
        $dividerLi.setAttribute("class", "divider-item1");
        $dividerLi.innerHTML = "<hr>";
        // Add divider to options.
        $menuOptions.append($dividerLi);
      }
      if (displayJira) {
        this.jiraIntegration($menuOptions);
      }
      if (displayBugDB) {
        this.bugDbIntegration($menuOptions);
      }
    }
  }

  private async jiraIntegration($menuOptions: Element) {
    try {
      const discussionEpic = await JiraIntegrationModel.getDiscussionEpic(
        this.discussion.discussionId
      );
      if (discussionEpic.status === 200) {
        const discussionEpicResponse: ApexGetDiscussionEpicRes200 =
          await discussionEpic.json();
        this.addJiraButton(
          $menuOptions,
          discussionEpicResponse.jira_detail_url
        );
      } else if (discussionEpic.status === 444) {
        const discussionEpicResponse: ApexGetDiscussionEpicRes444 =
          await discussionEpic.json();
        // Add JIRA dialog to body.
        this.$jiraDialog = <CCCJiraDialog>(
          document.createElement("ccc-jira-dialog")
        );
        document.querySelector("body").append(this.$jiraDialog);
        // Init dialog.
        this.$jiraDialog.uiUrl = discussionEpicResponse.apex_fr_src;
        this.$jiraDialog.onClose(() => {
          const $jiraAnchor = document.getElementById("cccJiraAnchor");
          if ($jiraAnchor) {
            $jiraAnchor.remove();
          }
          this.jiraIntegration($menuOptions);
        });
        // Add link to discussion menu.
        this.addJiraButton($menuOptions);
      }
    } catch (error) {
      console.error(error);
    }
  }

  private addJiraButton($menuOptions: Element, jiraUrl: string = null): void {
    // Create JIRA Epic button.
    const $jiraAnchor = document.createElement("a");
    $jiraAnchor.setAttribute("role", "menuitem");
    $jiraAnchor.classList.add("dropdown-menu-link");
    $jiraAnchor.setAttribute("tabindex", "0");
    if (!jiraUrl) {
      $jiraAnchor.innerHTML = "Create Jira Epic";
      $jiraAnchor.addEventListener("click", () => {
        this.$jiraDialog.open();
      });
    } else {
      $jiraAnchor.setAttribute("href", jiraUrl);
      $jiraAnchor.setAttribute("target", "_blank");
      $jiraAnchor.innerHTML = "Open Jira Epic";
    }
    const $jiraLi = document.createElement("li");
    $jiraLi.id = "cccJiraAnchor";
    $jiraLi.setAttribute("role", "presentation");
    $jiraLi.classList.add("1");
    $jiraLi.classList.add("no-icon");
    $jiraLi.append($jiraAnchor);
    // Add JIRA Epic button to options.
    $menuOptions.append($jiraLi);
  }

  private async bugDbIntegration($menuOptions: Element) {
    try {
      const bug = await BugDbIntegrationModel.getDiscussionBugCached(
        this.pageMetadata.Discussion.DiscussionID
      );
      if (bug.found) {
        this.addBugButton($menuOptions, bug.rptno);
      } else {
        // Add BugDB dialog to body.
        this.$bugDbDialog = <CCCBugDbDialog>(
          document.createElement("ccc-bug-db-dialog")
        );
        document.querySelector("body").append(this.$bugDbDialog);
        await this.$bugDbDialog.initDialog();
        // Init dialog.
        this.$bugDbDialog.bugDbUrl = process.env.BUG_DB_URL;
        // this.$bugDbDialog.onSubmit((): Promise<VMCreateEpicRes> => {
        //   console.log("submit");
        //   return null;
        // console.log("Discussion from API", this.discussion);
        // const newEpic: VMEpic = {
        //   subject: this.discussion.name,
        //   forumHash: this.discussion.categoryId.toString(),
        //   postHash: this.discussion.discussionId.toString(),
        //   userEmail: this.self.email,
        //   body: this.discussion.body.replace(/\n */gi, ""),
        //   projectId: this.$jiraDialog.jiraProject.origin.jiraid,
        // };
        // return JiraIntegrationModel.createEpic(newEpic);
        // });
        // Add Bug button to idea menu.
        this.addBugButton($menuOptions);
      }
    } catch (error) {
      console.error(error);
    }
  }

  private addBugButton($menuOptions: Element, rptno: string = null): void {
    // Create BugDB button.
    const $bugAnchor = document.createElement("a");
    $bugAnchor.setAttribute("role", "menuitem");
    $bugAnchor.classList.add("dropdown-menu-link");
    $bugAnchor.setAttribute("tabindex", "0");
    if (!rptno) {
      $bugAnchor.innerHTML = "Create ER (Status 15 Bug)";
      $bugAnchor.addEventListener("click", () => {
        this.$bugDbDialog.open();
      });
    } else {
      $bugAnchor.setAttribute("href", process.env.BUG_DB_URL + rptno);
      $bugAnchor.setAttribute("target", "_blank");
      $bugAnchor.innerHTML = "Open ER (Status 15 Bug)";
    }
    const $bugLi = document.createElement("li");
    $bugLi.setAttribute("role", "presentation");
    $bugLi.classList.add("1");
    $bugLi.classList.add("no-icon");
    $bugLi.append($bugAnchor);
    // Add BugDB button to options.
    $menuOptions.append($bugLi);
  }
}
